import './FastQuote.scss'

import { Backdrop, makeStyles, Modal } from '@material-ui/core'
import { DropDownButton, DropDownButtonItem } from '@progress/kendo-react-buttons'
import { AppBar, AppBarSection, AppBarSpacer, Menu } from '@progress/kendo-react-layout'
import { createAsyncThunk, isFulfilled, unwrapResult } from '@reduxjs/toolkit'
import clsx from 'clsx'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useMediaQuery } from 'react-responsive'
import { useParams } from 'react-router-dom'

import { useAppDispatch, useAppSelector } from '../app/hooks'
import { RootState } from '../app/store'
import {
    getContacts,
    getCustomers,
    getCustomerShipTos,
    getRequirements,
    LOADING_STATES,
    PACustomerContactViewRow,
    selectCustomer,
    selectFilteredAccounts,
    setAccountFilter,
    setCurrentSearch,
} from '../features/customer/customerSlice'
import { releaseOrderLock } from '../features/lock/lockSlice'
import { hasFastQuotePermissions, isWebUser } from '../features/meta/metaAPI'
import { closeModals, MODALS, setCustomerMode, setPreviewMode } from '../features/meta/metaSlice'
import {
    getOrderMiscInfo,
    loadOrder,
    needsSave,
    presaveCheck,
    quitQuote,
    saveOrder,
    searchQuote,
    setCurrentAccountNull,
    setHistoryDetailsNull,
    setLoadingState,
    setReviewingQuoteOutput,
    setSearchCustomerState,
    setToContactNull,
} from '../features/order/orderSlice'
import { FilterIcon } from '../GlobalComponents/_icons/Icons'
import { AWCButton, BUTTON_TYPES, FastQuoteInput } from '../GlobalComponents/Inputs/Inputs'
import {
    fqAlert,
    fqConfirmAllowNo,
    NotificationManager,
} from '../GlobalComponents/NotificationManager/NotificationManager'
import { getBaseURL } from '../GlobalComponents/URLs'
import CreatePreSaleAccount from './Components/CreatePreSaleAccount'
import CustomerAccountSelect from './Components/CustomerAccountSelect'
import CustomerSearch from './Components/CustomerSearch'
import { CustomerTypeModal } from './Components/CustomerTypeModal'
import IdentifierModal from './Components/IdentifierModal'
import OrderScreen from './Components/OrderScreen'
import RecipientModal from './Components/RecipientModal'
import { SearchQuotesModal } from './Components/SearchQuotesModal'
import ShippingModal from './Components/ShippingModal'
import { UniqueCustomerNameModal } from './Components/UniqueCustomerNameModal'
import styles from './FastQuote.module.scss'
import { fetchCustomers } from '../features/customer/customerAPI'

// import '@fontsource/roboto'
// import { getPricingTiers } from '../features/customer/customerSlice'
// import { hasFastQuotePermissions } from './features/meta/metaAPI'

const useStyles = makeStyles((theme) => ({
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        outline: 0,
    },
    paper: {
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[5],
        padding: theme.spacing(2, 4, 3),
    },
}))

const renderModal = (modal: MODALS, props: any) => {
    if (modal === MODALS.MODIFY) return <ShippingModal {...props} />
    else if (modal === MODALS.UPDATE) return <IdentifierModal {...props} />
    else if (modal === MODALS.CUSTOMER_TYPE) return <CustomerTypeModal {...props} />
    else if (modal === MODALS.UNIQUE_CUSTOMER_NAME) return <UniqueCustomerNameModal {...props} />
    else if (modal === MODALS.RECIPIENT) return <RecipientModal {...props} />
}

enum SCREENS {
    ORDER_SCREEN,
    SEARCH_SCREEN,
    CREATE_PRESALE_ACCOUNT_SCREEN,
    QUOTE_OUTPUT_SCREEN,
}

// Define App Bar Menu Items
function styleItems(items: any[], selectedIndex: number) {
    return items.map((item, index) => {
        let css =
            index === selectedIndex
                ? {
                      color: 'black',
                      fontWeight: '500',
                      borderBottomColor: 'red',
                      borderBottomWidth: 3,
                      borderBottomStyle: 'solid',
                      boxShadow: 'none',
                  }
                : { color: 'black', boxShadow: 'none' }
        return { ...item, cssStyle: css }
    })
}

const createMenuItems = (screen: SCREENS, hasLineItems: boolean) => {
    if (screen === SCREENS.SEARCH_SCREEN) {
        return styleItems(
            [
                {
                    key: 'menuSearchItemHome',
                    text: 'Home',
                    title: 'Fast Quote',
                    screen: SCREENS.SEARCH_SCREEN,
                },
            ],
            0
        )
    } else if (screen === SCREENS.CREATE_PRESALE_ACCOUNT_SCREEN) {
        return styleItems(
            [
                {
                    key: 'menuCreatePreSaleItemHome',
                    text: 'Home',
                    title: 'Fast Quote',
                    screen: SCREENS.SEARCH_SCREEN,
                },
            ],
            0
        )
    } else if (screen === SCREENS.ORDER_SCREEN) {
        if (hasLineItems) {
            return styleItems(
                [
                    {
                        key: 'menuOrderItemHome',
                        text: 'Home',
                        title: 'Fast Quote',
                        screen: SCREENS.SEARCH_SCREEN,
                    },
                    {
                        key: 'menuOrderItemRFQWorksheet',
                        text: 'RFQ Worksheet',
                        title: 'Fast Quote',
                        screen: SCREENS.ORDER_SCREEN,
                    },
                    {
                        key: 'menuOrderItemReview',
                        text: 'Review Quote',
                        title: 'Fast Quote',
                        screen: SCREENS.QUOTE_OUTPUT_SCREEN,
                    },
                ],
                1
            )
        } else {
            return styleItems(
                [
                    {
                        key: 'menuOrderLIItemHome',
                        text: 'Home',
                        title: 'Fast Quote',
                        screen: SCREENS.SEARCH_SCREEN,
                    },
                    {
                        key: 'menuOrderLIItemRFQWorksheet',
                        text: 'RFQ Worksheet',
                        title: 'Fast Quote',
                        screen: SCREENS.ORDER_SCREEN,
                    },
                ],
                1
            )
        }
    } else if (screen === SCREENS.QUOTE_OUTPUT_SCREEN) {
        if (hasLineItems) {
            return styleItems(
                [
                    {
                        key: 'menuQuoteItemHome',
                        text: 'Home',
                        title: 'Fast Quote',
                        screen: SCREENS.SEARCH_SCREEN,
                    },
                    {
                        key: 'menuQuoteItemRFQWorksheet',
                        text: 'RFQ Worksheet',
                        title: 'Fast Quote',
                        screen: SCREENS.ORDER_SCREEN,
                    },
                    {
                        key: 'menuQuoteItemReview',
                        text: 'Review Quote',
                        title: 'Fast Quote',
                        screen: SCREENS.QUOTE_OUTPUT_SCREEN,
                    },
                ],
                2
            )
        } else {
            return styleItems(
                [
                    {
                        key: 'menuQuoteLIItemHome',
                        text: 'Home',
                        title: 'Fast Quote',
                        screen: SCREENS.SEARCH_SCREEN,
                    },
                    {
                        key: 'menuQuoteLIItemRFQWorksheet',
                        text: 'RFQ Worksheet',
                        title: 'Fast Quote',
                        screen: SCREENS.ORDER_SCREEN,
                    },
                ],
                1
            )
        }
    }
}

export default function FastQuote() {
    document.title = 'Fast Quote'
    const searchInput = useRef<any>(null)
    const [startedSearch, setStartedSearch] = useState(false)
    const customerMode = useAppSelector((state) => state.meta.customerMode)
    const previewMode = useAppSelector((state) => state.meta.previewMode)
    const orderHeader = useAppSelector((state) => state.order.orderHeader)
    let lineItems = orderHeader.length === 1 ? orderHeader[0].OrderDetailView : []
    let customers = useAppSelector((state) => state.customer.CustomerAccounts)
    let customersLoading = useAppSelector((state) => state.customer.customersLoading)
    let loadingOrder = useAppSelector(
        (state) => state.order.orderLoading === LOADING_STATES.LOADING //&&
        // state.order.currentAccount &&
        // state.order.currentAccount.AccountCode !== '' &&
        // !state.order.currentAccount.OrderNumberExactMatch
    )
    let currentOrderAccount = useAppSelector((state) => state.order.currentAccount)
    // let currentOrder = useAppSelector((state) => state.order.orderHeader)
    let accountFilter = useAppSelector((state) => state.customer.currentFilter)
    const [creatingPreSaleAccount, setCreatingPreSaleAccount] = useState(false)
    const [initialLoading, setInitialLoading] = useState(true)
    const reviewingQuoteOutput = useAppSelector((state) => state.order.reviewingQuoteOutput)

    const dispatch = useAppDispatch()
    let modal = useAppSelector((state) => state.meta.currentModal)
    useEffect(() => {
        if (searchInput && searchInput.current) searchInput.current.focus()
    }, [])

    useEffect(() => {
        const releaseLock = () => dispatch(releaseOrderLock())
        window.addEventListener('beforeunload', releaseLock)
        return () => {
            window.removeEventListener('beforeunload', releaseLock)
        }
    }, [])

    let urlParams = useParams()

    // const getTiers = async () => {
    //     let res = await dispatch(getPricingTiers())
    //     // first request
    //     // we don't use this anymore for builds but it's convenient on dev since the cookie isnt on localhost
    //     if (res.meta.requestStatus === 'rejected') {
    //         // assuming this means not logged in
    //         window.location.href =
    //             getBaseURL() + `masterclient/main.aspx?redirect=${getBaseURL()}/FastQuote`
    //     }
    // }
    const initLoad = async () => {
        // load this up early -  why? JWM 3.31.22
        //getCurrentUser()
        //// getTiers()
        // if viewing through the web, don't require fast quote permissions
        let allowed = true // #14121 10/22/24 - everyone has permission so quote preview works.  isWebUser() || (await hasFastQuotePermissions())
        // let allowed = await hasFastQuotePermissions()

        //TODO maybe it's ok to start loading order before this check
        if (!allowed) {
            window.location.href = getBaseURL() + 'masterclient/main.aspx'
        }

        let search = window.location.search
        let params = new URLSearchParams(search.toLowerCase())
        let orderNumber = params.get('ordernumber')

        if (window.location.href.indexOf('/order/') > -1) {
            dispatch(setCustomerMode(true))
            if (typeof urlParams.orderNumber !== 'undefined') orderNumber = urlParams.orderNumber
        }

        // localhost doesnt have the cookie
        if (orderNumber) {
            let preview = params.get('preview')
            if (preview) dispatch(setPreviewMode(true))

            dispatch(
                loadOrder({
                    IncludeMiscInfo: true,
                    OrderNumber: orderNumber,
                    CalculateFreight: false,
                    CalculateTax: false,
                    IsWebUser: isWebUser(),
                    IsPreview: !!preview,
                })
            )
        }

        allowed && setInitialLoading(false)
    }

    useEffect(() => {
        dispatch(setCustomerMode(customerMode))
        if (customerMode) dispatch(setReviewingQuoteOutput(true))
    }, [customerMode])

    useEffect(() => {
        if (previewMode) dispatch(setReviewingQuoteOutput(true))
    }, [previewMode])

    useEffect(() => {
        initLoad()
    }, [])

    useLayoutEffect(() => {
        window.scrollTo(0, 0)
    }, [creatingPreSaleAccount])

    const currentScreen = getScreen()

    function getScreen() {
        if (currentOrderAccount && currentOrderAccount.AccountCode && !reviewingQuoteOutput) {
            if (
                currentOrderAccount &&
                currentOrderAccount.AccountCode === '' &&
                currentOrderAccount.OrderNumberExactMatch
            ) {
                return SCREENS.SEARCH_SCREEN // HOME
            }
            // WE'RE IN ORDER
            return SCREENS.ORDER_SCREEN
        } else if (creatingPreSaleAccount) {
            return SCREENS.CREATE_PRESALE_ACCOUNT_SCREEN
        } else if (currentOrderAccount && currentOrderAccount.AccountCode && reviewingQuoteOutput) {
            if (
                currentOrderAccount &&
                currentOrderAccount.AccountCode === '' &&
                currentOrderAccount.OrderNumberExactMatch
            ) {
                return SCREENS.SEARCH_SCREEN // HOME
            }
            return SCREENS.QUOTE_OUTPUT_SCREEN
        } else {
            // NOT IN ORDER
            return SCREENS.SEARCH_SCREEN // HOME
        }
    }
    // Handle Menu Selection
    async function handleMenuSelect(e: any) {
        // !! important  if they have temp changes in an input this gives time for the change to get to store for save check
        await new Promise((resolve) => setTimeout(resolve, 400))
        if (e.item.screen === SCREENS.SEARCH_SCREEN) {
            const shouldSave = needsSave()
            if (shouldSave) {
                const confirm = await fqConfirmAllowNo({
                    title: 'Unsaved changes',
                    message: 'Would you like to save before leaving?',
                })
                // null means cancel
                if (confirm === null) {
                    return
                }
                confirm && dispatch(saveOrder(null))
            }
            dispatch(setReviewingQuoteOutput(false))
            setCreatingPreSaleAccount(false)
            dispatch(setCurrentAccountNull())
            dispatch(quitQuote())
        }
        if (e.item.screen === SCREENS.ORDER_SCREEN) {
            dispatch(setReviewingQuoteOutput(false))
        }
        if (e.item.screen === SCREENS.QUOTE_OUTPUT_SCREEN) {
            let saveErrorPayload = await dispatch(presaveCheck(null))
            let saveError = saveErrorPayload.payload
            if (saveError) {
                fqAlert({
                    type: 'error',
                    title: 'Unsaved changes',
                    message: saveError,
                })
            } else {
                dispatch(setReviewingQuoteOutput(true))
                dispatch(getOrderMiscInfo(true))
            }
        }
    }
    const menuItems = createMenuItems(currentScreen, lineItems.length > 0)
    const classes = useStyles()
    // const [orderInputField, setOrderInputField] = useState('')
    // const [showLoadOption, setshowLoadOption] = useState(false)
    // useEffect(() => {
    //     let func = (e: KeyboardEvent) => {
    //         e.key === '/' && setshowLoadOption(true)
    //     }

    //     window.addEventListener('keypress', func)
    //     return () => window.removeEventListener('keypress', func)
    // }, [])

    const isLargeView = useMediaQuery({
        query: '(min-width: 1484px)',
    })
    let filteredCustomerResults = useAppSelector(selectFilteredAccounts)
    const currentSearch = useAppSelector((state) => state.customer.currentSearch)

    // Section for search quote on fast quote screen
    const [searchModalOpen, setSearchModalOpen] = useState<boolean>(false)
    const historyLoading = useAppSelector((state) => state.order.orderHistoryLoading)
    const historyDetails = useAppSelector((state) => state.order.orderHistoryDetails)
    const searchLoading = useAppSelector((state) => state.order.searchLoading)

    useEffect(() => {
        if (historyLoading === LOADING_STATES.DONE && currentScreen === SCREENS.SEARCH_SCREEN) {
            setSearchModalOpen(true)
        }
    }, [historyLoading])

    useEffect(() => {
        if (customers && customers.length === 1 && customers[0].OrderNumberExactMatch) {
            dispatch(searchQuoteStart(customers[0]))
        } else {
            dispatch(setSearchCustomerState(LOADING_STATES.INIT))
        }
    }, [customers])

    let searchQuoteStart = createAsyncThunk<any, any, { state: RootState }>(
        'order/searchQuoteStart',
        async (account: PACustomerContactViewRow, thunkAPI) => {
            dispatch(setSearchCustomerState(LOADING_STATES.LOADING))
            let promShipTos = dispatch(
                getCustomerShipTos(account.OrderNumberExactMatch!.CustomerAccountCode)
            )
            let promContacts = dispatch(
                getContacts(account.OrderNumberExactMatch!.CustomerAccountCode)
            )

            thunkAPI.dispatch(getRequirements(account.OrderNumberExactMatch!.CustomerAccountCode))
            let shipToResult = unwrapResult(await promShipTos)
            if (!account.AccountCode) {
                const data = await fetchCustomers(
                    account.OrderNumberExactMatch?.CustomerAccountCode || ''
                )
                thunkAPI.dispatch(selectCustomer(data[0]))
            } else {
                thunkAPI.dispatch(selectCustomer(account))
            }
            let defaultShipTo = shipToResult.find(
                (row) =>
                    row.ShipToAddress1 === account?.ShipToAddress1 &&
                    row.ShipToAddress2 === account.ShipToAddress2 &&
                    row.ShipToName === account.ShipToName &&
                    row.ShipToCity === account.ShipToCity &&
                    row.ShipToState === account.ShipToState &&
                    row.ShipToCountry === account.ShipToCountry &&
                    row.ShipToZipcode === account.ShipToZipcode
            )
            let contactResult = unwrapResult(await promContacts)
            let contact = contactResult.data.find(
                (row) =>
                    row.EmailAddress &&
                    row.EmailAddress!.toUpperCase() ===
                        account.OrderNumberExactMatch!.ContactEmail.toUpperCase().trim()
            )

            thunkAPI.dispatch(
                searchQuote({
                    account: account!,
                    shipTo: defaultShipTo!,
                    contactEmail: customers[0].OrderNumberExactMatch?.ContactEmail,
                    customerAccountCode: customers[0].OrderNumberExactMatch?.CustomerAccountCode,
                    contact,
                })
            )
        }
    )

    if (initialLoading) {
        return (
            <div className='fastQuote'>
                <div className={styles.app}>
                    <div className={'centered'}>
                        <div className={'k-i-loading'} style={{ fontSize: 200, height: '90vh' }} />
                    </div>
                    <NotificationManager key={'notificationsManager'} />
                </div>
            </div>
        )
    }
    if (
        (currentOrderAccount &&
            currentOrderAccount?.AccountCode &&
            !currentOrderAccount.OrderNumberExactMatch) ||
        reviewingQuoteOutput
    ) {
        return (
            <div className='fastQuote'>
                <div className={styles.app}>
                    {/* Allow bottom border to overlay app bar line*/}
                    {!customerMode && !previewMode && (
                        <AppBar style={{ paddingBottom: 'unset' }} className={styles.appHeader}>
                            <AppBarSection>
                                <Menu items={menuItems} onSelect={handleMenuSelect} />
                            </AppBarSection>
                            <AppBarSpacer />
                            <AppBarSection className='settings'>
                                {!reviewingQuoteOutput && (
                                    <>
                                        <AWCButton
                                            // barking because it wants an arg to use ThunkAPI in createAsyncThunk
                                            onClick={() => {
                                                dispatch(quitQuote())
                                            }}
                                            style={{
                                                flex: '80px 0 0',
                                                marginRight: 11,
                                            }}
                                            type={'secondary'}
                                        >
                                            Cancel
                                        </AWCButton>
                                        <AWCButton
                                            // barking because it wants an arg to use ThunkAPI in createAsyncThunk
                                            onClick={async () => {
                                                let prom = await dispatch(saveOrder(null))
                                                if (prom.meta.requestStatus === 'fulfilled') {
                                                    fqAlert({
                                                        type: 'success',
                                                        message: 'Save Successful!',
                                                    })
                                                } else {
                                                    fqAlert({
                                                        type: 'error',
                                                        message: prom.payload as string,
                                                        title: 'Save Failed',
                                                    })
                                                }
                                            }}
                                            style={{
                                                flex: '80px 0 0',
                                                marginRight: 11,
                                            }}
                                            type={BUTTON_TYPES.PRIMARY}
                                        >
                                            Save
                                        </AWCButton>
                                    </>
                                )}
                                {/* <OnBlurFastQuoteInput
                            placeholder="Load Order"
                            onChange={(val: string) => {
                                if (!val) return
                                dispatch(
                                    loadOrder({
                                        OrderNumber: val,
                                        IncludeMiscInfo: true,
                                    })
                                )
                            }}
                            style={{ marginRight: 10 }}
                        /> */}
                                <DropDownButton
                                    popupSettings={{ popupClass: 'legacyDropDown' }}
                                    icon='cog'
                                    onItemClick={async () => {
                                        let saveErrorPayload = await dispatch(presaveCheck(null))
                                        let saveError = saveErrorPayload.payload
                                        if (saveError) {
                                            fqAlert({
                                                type: 'error',
                                                title: 'Unsaved changes',
                                                message: saveError,
                                            })
                                            return
                                        } else {
                                            let saveValue = await dispatch(saveOrder(null))
                                            if (isFulfilled(saveValue)) {
                                                window.location.href = `${getBaseURL()}masterclient/main.aspx?OpenDialog=OrderEntry&OrderNumber=${
                                                    saveValue.payload.OrderHeaderView[0].OrderNumber
                                                }`
                                            }
                                        }
                                    }}
                                >
                                    <DropDownButtonItem text='Continue in Legacy .NET' />
                                </DropDownButton>
                            </AppBarSection>
                        </AppBar>
                    )}
                    {loadingOrder ? (
                        <div className={'centered'}>
                            <div
                                className={'k-i-loading'}
                                style={{ fontSize: 200, height: '90vh' }}
                            />
                        </div>
                    ) : (
                        <OrderScreen
                            // TODO remove these now that redux handles
                            reviewingQuoteOutput={reviewingQuoteOutput}
                            setReviewingQuoteOutput={() => dispatch(setReviewingQuoteOutput(false))}
                        />
                    )}
                    <NotificationManager key={'notificationsManager'} />
                </div>
            </div>
        )
    }
    if (creatingPreSaleAccount) {
        return (
            <div className='fastQuote'>
                <div className={styles.app}>
                    {/* Allow bottom border to overlay app bar line*/}
                    <AppBar style={{ paddingBottom: 'unset' }}>
                        <AppBarSection>
                            <Menu items={menuItems} onSelect={handleMenuSelect} />
                        </AppBarSection>
                        <AppBarSpacer />
                        <AppBarSection className='settings'>
                            <DropDownButton
                                popupSettings={{ popupClass: 'legacyDropDown' }}
                                icon='cog'
                                onItemClick={async () => {
                                    window.location.href = `${getBaseURL()}masterclient/main.aspx`
                                }}
                            >
                                <DropDownButtonItem text='Continue in Legacy .NET' />
                            </DropDownButton>
                        </AppBarSection>
                    </AppBar>
                    <div style={{ margin: 20, marginBottom: 0 }}>
                        <CreatePreSaleAccount
                            email={
                                currentSearch && currentSearch.includes('@') ? currentSearch : ''
                            }
                            back={() => {
                                dispatch(getCustomers(''))
                                dispatch(setCurrentSearch(''))
                                setCreatingPreSaleAccount(false)
                            }}
                        />
                    </div>
                    <NotificationManager key={'notificationsManager'} />
                </div>
            </div>
        )
    }
    if (searchModalOpen) {
        return (
            <div>
                {historyLoading === LOADING_STATES.LOADING || !historyDetails ? (
                    <>
                        {historyLoading === LOADING_STATES.LOADING && (
                            <div className={styles.historyDetail}>
                                <div
                                    style={{
                                        fontSize: 40,
                                        height: 40,
                                        marginRight: 100,
                                    }}
                                    className={'k-i-loading'}
                                />
                            </div>
                        )}
                        {historyLoading === LOADING_STATES.DONE && !historyDetails && <></>}
                    </>
                ) : (
                    <>
                        <Modal
                            open={searchModalOpen}
                            className={classes.modal}
                            onClose={(e: any, reason: string) => {
                                setSearchModalOpen(false)
                                dispatch(getCustomers(''))
                                dispatch(setCurrentSearch(''))
                                dispatch(setToContactNull())
                                dispatch(setHistoryDetailsNull())
                                dispatch(setLoadingState(LOADING_STATES.DONE))
                                dispatch(setSearchCustomerState(LOADING_STATES.INIT))
                            }}
                            aria-labelledby='simple-modal-title'
                            aria-describedby='simple-modal-description'
                            BackdropComponent={Backdrop}
                        >
                            <div>
                                <SearchQuotesModal
                                    hideFilter={true}
                                    close={() => {
                                        setSearchModalOpen(false)
                                        dispatch(getCustomers(''))
                                        dispatch(setCurrentSearch(''))
                                        dispatch(setToContactNull())
                                        dispatch(setHistoryDetailsNull())
                                        dispatch(setLoadingState(LOADING_STATES.DONE))
                                        dispatch(setSearchCustomerState(LOADING_STATES.INIT))
                                    }}
                                    onSubmit={() => {
                                        setSearchModalOpen(false)
                                    }}
                                    filter={{
                                        field: 'OrderNumber',
                                        value: customers[0].OrderNumberExactMatch!.OrderNumber,
                                    }}
                                    selectedOrder={customers[0].OrderNumberExactMatch!.OrderNumber}
                                    contactEmail={customers[0].OrderNumberExactMatch!.ContactEmail}
                                    accountCode={
                                        customers[0].OrderNumberExactMatch!.CustomerAccountCode
                                    }
                                />
                            </div>
                        </Modal>
                    </>
                )}
            </div>
        )
    }
    return (
        <div className='fastQuote'>
            <div className={styles.app}>
                {/* Allow bottom border to overlay app bar line*/}
                <AppBar style={{ paddingBottom: 'unset' }} className={styles.appHeader}>
                    <AppBarSection>
                        <Menu items={menuItems} onSelect={handleMenuSelect} />
                    </AppBarSection>
                    <AppBarSpacer />
                    <AppBarSection className='settings'>
                        {/* <OnBlurFastQuoteInput
                        onChange={(val: string) => {
                            if (!val) return
                            dispatch(
                                loadOrder({
                                    OrderNumber: val,
                                    IncludeMiscInfo: true,
                                })
                            )
                        }}
                        placeholder="Load Order"
                        style={{ marginRight: 10 }}
                    /> */}
                        <DropDownButton
                            popupSettings={{ popupClass: 'legacyDropDown' }}
                            icon='cog'
                            onItemClick={async () => {
                                window.location.href = `${getBaseURL()}masterclient/main.aspx`
                            }}
                        >
                            <DropDownButtonItem text='Continue in Legacy .NET' />
                        </DropDownButton>
                    </AppBarSection>
                </AppBar>
                {loadingOrder ? (
                    <div className={'centered'}>
                        <div className={'k-i-loading'} style={{ fontSize: 200, height: '90vh' }} />
                    </div>
                ) : (
                    <>
                        <div
                            className={clsx([styles.container, styles.customerSearch])}
                            style={{ display: 'flex', gap: 40, paddingBottom: 20 }}
                        >
                            <CustomerSearch
                                title='Search'
                                inputRef={searchInput}
                                onSearch={() => {
                                    // clear filter
                                    dispatch(setAccountFilter(''))
                                    setStartedSearch(true)
                                }}
                            />
                            <div
                                className={clsx([styles.container, styles.accountFilter])}
                                style={{
                                    marginBottom:
                                        startedSearch && customers.length > window.innerWidth / 397
                                            ? -25
                                            : 0,
                                }}
                            >
                                {startedSearch && customers.length > window.innerWidth / 397 && (
                                    <FastQuoteInput
                                        Icon={FilterIcon}
                                        value={accountFilter}
                                        onChange={(value: string) =>
                                            dispatch(setAccountFilter(value))
                                        }
                                        placeholder={'Filter Search Results'}
                                        renderEndIcon={() =>
                                            accountFilter && (
                                                <div
                                                    style={{ width: 18, height: 15 }}
                                                    className={'a-i-close'}
                                                    onClick={() => {
                                                        dispatch(setAccountFilter(''))
                                                    }}
                                                    data-testid='filterSearchResultsClear'
                                                />
                                            )
                                        }
                                        testid='filterSearchResults'
                                    />
                                )}
                            </div>
                        </div>
                        {searchLoading !== LOADING_STATES.LOADING && (
                            <div className={clsx([styles.container, styles.searchContainer])}>
                                <CustomerAccountSelect />
                            </div>
                        )}
                        {searchLoading === LOADING_STATES.LOADING ? (
                            <div className={'centered'}>
                                <div
                                    className={'k-i-loading'}
                                    style={{ fontSize: 200, height: 580 }}
                                />
                            </div>
                        ) : (
                            customersLoading !== LOADING_STATES.INIT &&
                            customersLoading !== LOADING_STATES.LOADING &&
                            currentSearch && (
                                // if it's large view leave room to scroll -- on small view we're squeezing to fit with no scroll
                                <div
                                    className={styles.container}
                                    style={{
                                        marginBottom: isLargeView ? 50 : 0,
                                        position: 'relative',
                                    }}
                                >
                                    <div
                                        style={
                                            isLargeView ||
                                            filteredCustomerResults.length === 0 ||
                                            (filteredCustomerResults.length === 1 &&
                                                filteredCustomerResults[0].OrderNumberExactMatch)
                                                ? {}
                                                : { position: 'absolute', top: -40 }
                                        }
                                    >
                                        Not the customer you are looking for?
                                        <div
                                            className={styles.createPreSaleButton}
                                            onClick={() => setCreatingPreSaleAccount(true)}
                                        >
                                            <span className={styles.plusButton} />
                                            <span data-testid='createPreSaleAccountLink'>
                                                Create Pre-Sale Customer
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )
                        )}
                    </>
                )}
                <Modal
                    open={modal !== MODALS.NONE}
                    className={classes.modal}
                    onClose={() => dispatch(closeModals())}
                    aria-labelledby='simple-modal-title'
                    aria-describedby='simple-modal-description'
                    BackdropComponent={Backdrop}
                >
                    {/* <div className={styles.modalContainer}><ShippingModal /></div> */}
                    <div className={styles.modalContainer}>
                        {renderModal(modal, {
                            close: () => dispatch(closeModals()),
                        })}
                    </div>
                </Modal>
                <NotificationManager key={'notificationsManager'} />
            </div>
        </div>
    )
}
